// The ACC cloud on a project card: active (blue) when the project is linked to a Revit
// file in ACC. Opens the link window: browse hubs > projects > folders and pick a .rvt
// (linking queues the server export at once), or for a linked project see its file and
// run, sync now, change the file or unlink.
import React, { useEffect, useState } from "react";
import { Modal, Spin } from "antd";
import { FolderOutlined, FileOutlined, LoadingOutlined } from "@ant-design/icons";
import addNotification from "helpers/notify";
import AccCloudIcon from "./AccCloudIcon";
import { AutodeskLogo } from "components/Integrations/ProviderLogos";
import { useAccLink, setAccLink, refreshAccLinks, isAccBusy } from "./useAccLinks";
import { useAccStatus, connectAcc } from "./AccConnectionPanel";
import { formatEta } from "./AccCardStatus";
import { getAccHubs, getAccProjects, getAccTopFolders, getAccFolder, linkAccFile, syncAccLink, unlinkAcc } from "./accApi";
import "./acc.scss";

// crumbs: [{ kind: 'root' }, { kind: 'hub', id, name }, { kind: 'project', id, name }, { kind: 'folder', id, name }...]
function MissingHubHint({ clientId }) {
  const [copied, setCopied] = useState(false);
  const copy = () =>
    navigator.clipboard &&
    navigator.clipboard.writeText(clientId).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  return (
    <div className="acc-hub-hint">
      <b>Don't see a hub, such as a client's?</b>
      <p>
        Autodesk only shows hubs that allow BidLight. Ask that hub's ACC account admin to open Account Admin &gt; Custom Integrations &gt; Add
        custom integration, paste this Client ID and give it Document Management access. The hub then appears here.
      </p>
      {clientId ? (
        <div className="acc-hub-hint-id">
          <code>{clientId}</code>
          <button onClick={copy}>{copied ? "Copied" : "Copy"}</button>
        </div>
      ) : null}
    </div>
  );
}

function Browser({ onPick, picking, clientId }) {
  const [crumbs, setCrumbs] = useState([{ kind: "root", name: "Hubs" }]);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const here = crumbs[crumbs.length - 1];
  const hub = crumbs.find((c) => c.kind === "hub");
  const proj = crumbs.find((c) => c.kind === "project");

  useEffect(() => {
    let live = true;
    setRows(null);
    setError("");
    const load = async () => {
      if (here.kind === "root") {
        return (await getAccHubs()).map((h) => ({ kind: "hub", id: h.id, name: h.name }));
      }
      if (here.kind === "hub") {
        return (await getAccProjects(here.id)).map((p) => ({ kind: "project", id: p.id, name: p.name }));
      }
      const items = here.kind === "project" ? await getAccTopFolders(hub.id, here.id) : await getAccFolder(proj.id, here.id);
      return items
        .map((e) => ({ kind: e.type === "folder" ? "folder" : "item", id: e.id, name: e.name, isRevit: e.isRevit }))
        .sort((a, b) => (a.kind === b.kind ? a.name.localeCompare(b.name) : a.kind === "folder" ? -1 : 1));
    };
    load()
      .then((r) => live && setRows(r))
      .catch((e) => live && setError(e.message || "Could not read ACC"));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crumbs.length, here.id]);

  const open = (row) => {
    if (row.kind === "item") {
      if (!row.isRevit || picking) return;
      const folder = crumbs.filter((c) => c.kind === "folder").pop();
      onPick({
        hubId: hub.id,
        accProjectId: proj.id,
        itemId: row.id,
        folderId: folder ? folder.id : "",
        fileName: row.name,
        path: [...crumbs.slice(1).map((c) => c.name), row.name].join(" / "),
      });
      return;
    }
    setCrumbs([...crumbs, row]);
  };

  return (
    <div className="acc-browser">
      <div className="acc-crumbs">
        {crumbs.map((c, i) => (
          <span key={`${c.kind}-${c.id || i}`}>
            {i > 0 ? " / " : ""}
            {i < crumbs.length - 1 ? <button onClick={() => setCrumbs(crumbs.slice(0, i + 1))}>{c.name}</button> : <b>{c.name}</b>}
          </span>
        ))}
      </div>
      {error ? <p className="text-danger">{error}</p> : null}
      {!rows && !error ? <Spin /> : null}
      {rows && !rows.length ? <p className="text-muted">Nothing here.</p> : null}
      {/* Autodesk lists only the hubs whose account admin has added this
          app as a custom integration - a client's hub stays hidden until
          they do, however many projects the user is a member of there. */}
      {here.kind === "root" && rows ? <MissingHubHint clientId={clientId} /> : null}
      {rows &&
        rows.map((r) => (
          <button
            key={r.id}
            className="acc-row"
            disabled={r.kind === "item" && !r.isRevit}
            title={r.kind === "item" && !r.isRevit ? "Only Revit (.rvt) files can be linked" : undefined}
            onClick={() => open(r)}
          >
            {r.kind === "item" ? <FileOutlined /> : <FolderOutlined />}
            <span>{r.name}</span>
            {r.kind === "item" && r.isRevit ? <small className="ml-auto text-bid">{picking ? <LoadingOutlined /> : "Link"}</small> : null}
          </button>
        ))}
    </div>
  );
}

export default function AccLinkButton({ projectId, projectTitle, canEdit, connected = false }) {
  const link = useAccLink(projectId);
  const [open, setOpen] = useState(false);
  const [changing, setChanging] = useState(false);
  const [working, setWorking] = useState(false);
  const linked = !!link;
  const busy = isAccBusy(link);

  const pick = async (body) => {
    setWorking(true);
    try {
      const r = await linkAccFile({ projectId, ...body });
      setAccLink(projectId, r.link);
      addNotification(`Linked to ${body.fileName} - the model is being processed`, "success");
      setChanging(false);
      setOpen(false);
    } catch (e) {
      addNotification(e.message || "Could not link the file", "danger");
    } finally {
      setWorking(false);
    }
  };

  const sync = async () => {
    setWorking(true);
    try {
      const r = await syncAccLink(projectId);
      setAccLink(projectId, r.link);
      addNotification(r.queued ? "Sync queued" : `Already ${r.already}`, "success");
    } catch (e) {
      addNotification(e.message, "danger");
    } finally {
      setWorking(false);
    }
  };

  const unlink = () =>
    Modal.confirm({
      title: `Unlink "${projectTitle}" from ACC?`,
      content: "The published model stays. The project stops updating from ACC.",
      okText: "Unlink",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await unlinkAcc(projectId);
          setAccLink(projectId, null);
          refreshAccLinks();
          setOpen(false);
        } catch (e) {
          addNotification(e.message, "danger");
        }
      },
    });

  return (
    <>
      <button
        title={linked ? `Linked to ACC: ${link.path || link.fileName}` : "Link to a Revit model in Autodesk Construction Cloud"}
        className={`pc-tool acc-cloud-btn ${linked ? "linked" : ""}`}
        onClick={() => setOpen(true)}
      >
        {busy ? (
          <Spin indicator={<LoadingOutlined style={{ fontSize: 16, color: "#0696D7" }} spin />} />
        ) : (
          <AutodeskLogo connected={connected || linked} linked={linked} size={22} />
        )}
      </button>
      {open && (
        <AccLinkModal
          link={link}
          projectTitle={projectTitle}
          canEdit={canEdit}
          changing={changing || !linked}
          setChanging={setChanging}
          working={working}
          onPick={pick}
          onSync={sync}
          onUnlink={unlink}
          onClose={() => {
            setOpen(false);
            setChanging(false);
          }}
        />
      )}
    </>
  );
}

function AccLinkModal({ link, projectTitle, canEdit, changing, setChanging, working, onPick, onSync, onUnlink, onClose }) {
  const { status, loading } = useAccStatus();
  const run = link && link.run;
  const busy = isAccBusy(link);

  let content;
  if (loading && !status) content = <Spin />;
  else if (status && status.configured === false) content = <p>Autodesk Construction Cloud is not set up on this server yet.</p>;
  else if (changing && !canEdit) content = <p>Only people who can edit this project can link it to ACC.</p>;
  else if (changing && !(status && status.connected)) {
    content = (
      <div>
        <p>Connect your company&apos;s Autodesk account first, then pick the Revit file for this project.</p>
        <button className="header-btn text-white bg-bid border-bid px-3" onClick={connectAcc}>
          Connect ACC
        </button>
      </div>
    );
  } else if (changing) content = <Browser onPick={onPick} picking={working} clientId={status.clientId} />;
  else {
    content = (
      <div>
        <p className="mb-1">
          <AccCloudIcon active size={16} /> <b>{link.fileName}</b>
        </p>
        <p className="text-muted mb-2" style={{ wordBreak: "break-word" }}>
          {link.path}
        </p>
        {run ? (
          <p className="mb-2">
            {busy
              ? `${run.status === "queued" ? "Queued" : `Processing (${run.stage}, ${Math.round((run.progress || 0) * 100)}%)`} - ready in ${formatEta(run.etaSeconds)}`
              : run.status === "failed"
              ? `Last run failed: ${run.error}`
              : `Synced ACC v${link.lastVersionNumber || run.versionNumber}${run.durationSeconds ? ` in ${formatEta(run.durationSeconds).replace("~", "")}` : ""}`}
          </p>
        ) : null}
        {run && run.warnings && run.warnings.length ? (
          <ul className="small text-muted">
            {run.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        ) : null}
        {link.checkNote ? <p className="small text-muted">Last ACC check: {link.checkNote}</p> : null}
        {canEdit ? (
          <div className="d-flex flex-wrap" style={{ gap: 8 }}>
            <button className="header-btn text-white bg-bid border-bid px-3" disabled={busy || working} onClick={onSync}>
              Sync now
            </button>
            <button className="bg-white text-bid border-bid shadow-none px-3" disabled={working} onClick={() => setChanging(true)}>
              Change file
            </button>
            <button className="bg-white text-danger border-danger shadow-none px-3" disabled={working} onClick={onUnlink}>
              Unlink
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <Modal open title={`ACC - ${projectTitle}`} onCancel={onClose} footer={null} width={560} destroyOnClose>
      {content}
    </Modal>
  );
}
